import { loadRetrofitSurveyFromSupabase, saveRetrofitSurveyToSupabase } from './retrofitSurveyStorage';
import type { RetrofitSurveyData } from './retrofitSurveyStorage';

/**
 * Clean up base64 data from photos that have been successfully uploaded to Supabase
 * This removes dataUrl and base64String fields from photo objects that have a valid URL
 */
export const cleanupSurveyPhotos = async (surveyId: string): Promise<{ 
  success: boolean; 
  beforeSize: number; 
  afterSize: number;
  photosProcessed: number;
  error?: string;
}> => {
  try {
    console.log(`🧹 [Photo Cleanup] Starting cleanup for survey: ${surveyId}`);
    
    // Load the survey
    const survey = await loadRetrofitSurveyFromSupabase(surveyId);
    const beforeSize = new Blob([JSON.stringify(survey)]).size;
    
    console.log(`📊 [Photo Cleanup] Original size: ${(beforeSize / 1024 / 1024).toFixed(2)} MB`);
    
    let photosProcessed = 0;
    
    // Helper function to clean a photo object
    const cleanPhoto = (photo: any): any => {
      if (!photo) return photo;
      
      // If photo has a URL (successfully uploaded), remove base64 data
      if (photo.url && photo.url.startsWith('http')) {
        photosProcessed++;
        return {
          displayId: photo.displayId,
          format: photo.format,
          url: photo.url,
          uploading: false,
          error: undefined,
          // Keep these if they exist
          ...(photo.caption && { caption: photo.caption }),
          ...(photo.note && { note: photo.note }),
          ...(photo.timestamp && { timestamp: photo.timestamp }),
          ...(photo.order !== undefined && { order: photo.order })
        };
      }
      
      // If no URL yet, keep as is (still uploading or failed)
      return photo;
    };
    
    // Helper to clean array of photos
    const cleanPhotoArray = (photos: any[]): any[] => {
      if (!Array.isArray(photos)) return photos;
      return photos.map(cleanPhoto).filter(Boolean);
    };
    
    // Clean initial questionnaire photos
    if (survey.initial_questionnaire?.photos) {
      Object.keys(survey.initial_questionnaire.photos).forEach(key => {
        const section = survey.initial_questionnaire.photos[key];
        if (section?.photos) {
          section.photos = cleanPhotoArray(section.photos);
        }
      });
    }
    
    // Clean heating system photos
    if (survey.heating_system?.photos) {
      Object.keys(survey.heating_system.photos).forEach(key => {
        const section = survey.heating_system.photos![key];
        if (section?.photos) {
          section.photos = cleanPhotoArray(section.photos);
        }
      });
    }
    
    // Clean property elevation photos
    const cleanPropertyPhotos = (property: any) => {
      if (!property) return;
      
      // Array fields
      const arrayFields = [
        'detachmentType',
        'orientationImage',
        'frontElevationImage',
        'roofTypeEvidenceImages',
        'gutteringDrainageImages',
        'wallThicknessImages',
        'wallDefectImages',
        'rearElevationImage',
        'rearBackyardImages',
        'rightElevationImage',
        'rightSideImages',
        'leftElevationImage',
        'leftSideImages'
      ];
      
      arrayFields.forEach(field => {
        if (Array.isArray(property[field])) {
          property[field] = cleanPhotoArray(property[field]);
        }
      });
      
      // Single photo fields
      const singleFields = [
        'wallTypeEvidenceImage'
      ];
      
      singleFields.forEach(field => {
        if (Array.isArray(property[field])) {
          property[field] = cleanPhotoArray(property[field]);
        } else if (property[field]) {
          property[field] = cleanPhoto(property[field]);
        }
      });
    };
    
    cleanPropertyPhotos(survey.property_front);
    cleanPropertyPhotos(survey.property_rear);
    cleanPropertyPhotos(survey.property_left);
    cleanPropertyPhotos(survey.property_right);
    
    // Clean room photos (these are string arrays, just URLs)
    // No cleanup needed for room photos as they're already just URLs
    
    const afterSize = new Blob([JSON.stringify(survey)]).size;
    const savedMB = ((beforeSize - afterSize) / 1024 / 1024).toFixed(2);
    
    console.log(`📊 [Photo Cleanup] After cleanup: ${(afterSize / 1024 / 1024).toFixed(2)} MB`);
    console.log(`✨ [Photo Cleanup] Saved: ${savedMB} MB`);
    console.log(`📸 [Photo Cleanup] Photos processed: ${photosProcessed}`);
    
    // Save the cleaned survey
    console.log(`💾 [Photo Cleanup] Saving cleaned survey...`);
    await saveRetrofitSurveyToSupabase(survey.name, survey, surveyId);
    
    console.log(`✅ [Photo Cleanup] Cleanup complete!`);
    
    return {
      success: true,
      beforeSize,
      afterSize,
      photosProcessed
    };
    
  } catch (error) {
    console.error('❌ [Photo Cleanup] Error cleaning survey:', error);
    return {
      success: false,
      beforeSize: 0,
      afterSize: 0,
      photosProcessed: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Clean up all retrofit surveys in the database
 * WARNING: This will process ALL surveys - use with caution!
 */
export const cleanupAllSurveys = async (): Promise<{
  processed: number;
  successful: number;
  failed: number;
  totalSaved: number;
}> => {
  console.log('🧹 [Photo Cleanup] Starting cleanup for ALL surveys...');
  console.log('⚠️ [Photo Cleanup] This may take a while...');
  
  // This would require fetching all survey IDs first
  // Implementation left for future if needed
  
  return {
    processed: 0,
    successful: 0,
    failed: 0,
    totalSaved: 0
  };
};
